import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IUser } from '@/features/user/store/user.model';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import {
  TKycStatusConfig,
  TConfigUserRole,
  configUserRole,
  kycStatusConfig,
} from '@/store/models/dashboard-config.model';

import { kycStatusColor, TKycStatus } from '@/features/kyc/store/models/kyc.model';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

export function useKycStatus(user: IUser) {
  const config = useDashboardConfig();
  const roleKycStatusConfig = config?.settings.users?.kyc?.status;
  const { t } = useTranslation();

  const hasKycStatusInConfig = useCallback(
    (status: TKycStatusConfig, userRole?: TConfigUserRole) => {
      return userRole ? roleKycStatusConfig?.[userRole] === status : false;
    },
    [roleKycStatusConfig],
  );

  const kycStatus: TKycStatus = useMemo(() => {
    if (user) {
      const { kycReview, kycLevel, userTypeId, controllingPersonType } = user;
      const isPhysicalUser = userTypeId === 1;
      const userRole = DashboardHelpers.getUserRole(user);
      let roleInConfig: TConfigUserRole;

      if (userRole === configUserRole.selfEmployed || userRole === configUserRole.employee) {
        roleInConfig = userRole;
      } else if (['child', 'minor'].includes(userRole)) {
        roleInConfig = configUserRole.child;
      } else if (controllingPersonType !== 1 && controllingPersonType !== 3)
        roleInConfig = configUserRole.otherPhysical;

      if (isPhysicalUser) {
        if (
          kycReview === 0 &&
          (kycLevel === 2 || (kycLevel === 1 && hasKycStatusInConfig(kycStatusConfig.notRequired, roleInConfig)))
        ) {
          return 'not_required';
        }
        if (kycLevel === 1 && kycReview === 0 && hasKycStatusInConfig(kycStatusConfig.optional, roleInConfig)) {
          return 'optional';
        }
      }

      if (
        (kycLevel === 0 && kycReview === 0) ||
        (isPhysicalUser &&
          kycLevel === 1 &&
          kycReview === 0 &&
          hasKycStatusInConfig(kycStatusConfig.required, roleInConfig))
      ) {
        return 'to_submit_to_treezor';
      }
      if ([1, 7].includes(kycReview)) return 'pending_treezor';
      if (kycLevel !== 4 && kycReview === 3) return 'to_review';
      if (kycReview === 6) return 'to_check';
      if (kycReview === 2 || (userTypeId !== 1 && kycLevel === 2 && kycReview === 0)) return 'validated';
      if (kycLevel === 4 && kycReview === 3) return 'refused';
    }
    return 'to_submit_to_treezor';
  }, [hasKycStatusInConfig, user]);

  const kycStatusBadgeProps = useMemo(
    () => ({
      label: t(`components.molecules.statusLabel.${kycStatus.toUpperCase()}`),
      color: kycStatusColor[kycStatus],
    }),
    [kycStatus, t],
  );

  return {
    kycReview: user?.kycReview,
    kycLevel: user?.kycLevel,
    kycStatus,
    kycStatusBadgeProps,
  };
}
